import { Link } from 'react-router-dom'
import { t } from '../i18n.ts'
import { FRIEND_FALLBACK_PREFIX } from '../domain/friendName.ts'
import type { SharedAnnotation as Shared } from '../store/types.ts'
import { useAuth } from '../store/AuthProvider.tsx'
import { ThumbDownIcon, ThumbUpIcon } from './ConcertRow.tsx'
import { DiceIcon } from './DiceControls.tsx'
import './SharedAnnotation.css'

/**
 * A note a friend shared, shown above the reader's own controls.
 *
 * Rendered on every concert route, not only behind a `?delt=` link: a signed-in
 * recipient is in the annotation's `sharedWith`, so their live query already has it,
 * and R19 promises they see it — including when they reach the concert from the day
 * list rather than from the notification.
 */
export function SharedAnnotationList({
  shared,
  loading,
  failed,
  onRetry,
}: {
  shared: readonly Shared[]
  loading?: boolean
  failed?: boolean
  onRetry?: () => void
}) {
  const { cloudEnabled, user, signIn } = useAuth()

  if (loading) {
    return (
      <div className="shared shared--placeholder" aria-busy="true">
        {t.shared.loading}
      </div>
    )
  }

  if (failed) {
    return (
      <div className="shared shared--failed" role="status">
        <span>{t.shared.failed}</span>
        {onRetry && (
          <button type="button" className="shared__retry" onClick={onRetry}>
            {t.shared.retry}
          </button>
        )}
      </div>
    )
  }

  if (shared.length === 0) return null

  // A recipient who followed an emailed link is by definition not signed in. The
  // note itself reached them in the email body, so this is an upgrade prompt, not a
  // gate on the content.
  if (cloudEnabled && !user) {
    return (
      <div className="shared shared--signin">
        <p className="shared__signin-text">{t.shared.signInHelp}</p>
        <button type="button" className="shared__signin-button" onClick={() => void signIn()}>
          {t.shared.signInToSee}
        </button>
      </div>
    )
  }

  return (
    <>
      {shared.map((annotation) => (
        <article className="shared" key={`${annotation.uid}-${annotation.concertId}`}>
          <p className="shared__from">{t.shared.from(annotation.ownerName ?? FRIEND_FALLBACK_PREFIX)}</p>
          {annotation.vote && (
            <p className={`shared__vote shared__vote--${annotation.vote}`}>
              {annotation.vote === 'up' ? <ThumbUpIcon /> : <ThumbDownIcon />}
              <span>{annotation.vote === 'up' ? t.concert.thumbsUp : t.concert.thumbsDown}</span>
            </p>
          )}
          {/*
            The die belongs here as much as the thumb does. The day row shows a
            friend's terningkast beside their name, and a field that appears in the
            list and then vanishes on the page the list opens reads as data lost. Both
            can be set at once — a thumb is a plan, a die is a verdict — so this is
            beside the vote rather than instead of it.
          */}
          {annotation.dice != null && (
            <p className="shared__dice">
              <DiceIcon value={annotation.dice} />
              <span>{t.concert.diceValue(annotation.dice)}</span>
            </p>
          )}
          {annotation.note && <blockquote className="shared__note">{annotation.note}</blockquote>}
        </article>
      ))}
    </>
  )
}

/**
 * One row in the shared-with-me list.
 *
 * A die and the words, the same two things a friend's row on that page carries, and for
 * the same reason: they are what still reads at a distance from the concert they belong
 * to. The thumb stays behind on the day list and the concert page — this row is read in
 * a stack of other people's rows, where a plan without its time and stage says nothing.
 *
 * Unlike a friend's row, though, every one of these is drawn whatever it holds. It is
 * here because somebody deliberately sent it (R34), and a share reduced to nothing by a
 * rendering rule is a message that silently failed to arrive.
 */
export function SharedRow({ annotation, concertName }: { annotation: Shared; concertName: string }) {
  return (
    <li className="shared-row">
      <Link to={`/konsert/${annotation.concertId}`} className="shared-row__link">
        <span className="shared-row__name">{concertName}</span>
        <span className="shared-row__from">
          {t.shared.from(annotation.ownerName ?? FRIEND_FALLBACK_PREFIX)}
          {annotation.dice != null && (
            <span className="shared-row__dice" title={t.concert.diceValue(annotation.dice)}>
              <DiceIcon value={annotation.dice} />
              <span className="sr-only">{t.concert.diceValue(annotation.dice)}</span>
            </span>
          )}
        </span>
        {annotation.note && <span className="shared-row__note">{annotation.note}</span>}
      </Link>
    </li>
  )
}
